import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Home, ChevronRight, ShieldAlert, Clock, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { MobileBottomNav } from '@/components/mobile/MobileNavigation';
import MobileAccessDenied from '@/components/mobile/MobileAccessDenied';

const STATUS_TABS = ['PENDING', 'APPROVED', 'REJECTED'];

export default function MobileCheckinExceptionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [items, setItems] = useState([]);
  const [labels, setLabels] = useState({});
  const [status, setStatus] = useState('PENDING');
  const [reviewing, setReviewing] = useState(null); // exception being reviewed
  const [decision, setDecision] = useState(null);    // APPROVED | REJECTED
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/mobile/checkin-exceptions?status=${status}`);
      const d = res.data || {};
      if (d.canReview === false) { setForbidden(true); return; }
      setItems(d.items || []);
      setLabels(d.reasonLabels || {});
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error('Could not load exceptions');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openReview = (exc, dec) => { setReviewing(exc); setDecision(dec); setNotes(''); };

  const submitReview = async () => {
    if (!reviewing || !decision) return;
    if (decision === 'REJECTED' && !notes.trim()) {
      toast.error('Please add a note when rejecting');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/mobile/checkin-exceptions/${reviewing._id || reviewing.id}/review`, {
        decision, notes: notes.trim() || undefined,
      });
      toast.success(`Exception ${decision.toLowerCase()}`);
      setReviewing(null); setDecision(null); setNotes('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Review failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (forbidden) {
    return (
      <MobileAccessDenied
        title="Reviewer access only"
        message="Only coordinators and admins can review check-in exceptions."
        onBack={() => navigate('/mobile')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="mobile-checkin-exceptions">
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="-ml-2" onClick={() => navigate('/mobile')} data-testid="exceptions-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <button onClick={() => navigate('/mobile')} className="flex items-center gap-1 hover:text-foreground">
                <Home className="h-3 w-3" /> Home
              </button>
              <ChevronRight className="h-3 w-3" /> <span>Check-in Exceptions</span>
            </div>
            <h1 className="text-base font-bold">Check-in Exceptions</h1>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="grid grid-cols-3 w-full">
            {STATUS_TABS.map((s) => (
              <TabsTrigger key={s} value={s} data-testid={`tab-${s.toLowerCase()}`}>
                {s.charAt(0) + s.slice(1).toLowerCase()}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-xl" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-muted-foreground py-12" data-testid="exceptions-empty">
            <ShieldAlert className="h-10 w-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium">No {status.toLowerCase()} exceptions</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((exc) => (
              <Card key={exc._id || exc.id} data-testid="exception-card">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-semibold">{(exc.kind || '').replace('_', ' ')}</span>
                    </div>
                    <Badge variant={exc.status === 'PENDING' ? 'secondary' : exc.status === 'APPROVED' ? 'default' : 'destructive'}>
                      {exc.status}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(exc.reasons || []).map((r) => (
                      <Badge key={r} variant="outline" className="text-[10px]">{labels[r] || r}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {exc.user_name || exc.user_id} · {exc.created_at?.slice(0, 16).replace('T', ' ')}
                  </p>
                  {exc.details?.variance_reason && (
                    <p className="text-xs" data-testid="exception-variance-reason">
                      <span className="text-muted-foreground">Staff reason:</span> {exc.details.variance_reason}
                    </p>
                  )}
                  {exc.review_notes && <p className="text-xs">Note: {exc.review_notes}</p>}
                  {exc.status === 'PENDING' && (
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => openReview(exc, 'APPROVED')} data-testid="approve-btn">
                        <CheckCircle className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="destructive" className="flex-1" onClick={() => openReview(exc, 'REJECTED')} data-testid="reject-btn">
                        <XCircle className="h-4 w-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!reviewing} onOpenChange={(v) => { if (!v) { setReviewing(null); setDecision(null); } }}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>{decision === 'APPROVED' ? 'Approve' : 'Reject'} exception</DialogTitle>
            <DialogDescription className="text-xs">
              {decision === 'REJECTED' ? 'A note is required when rejecting.' : 'Optionally add a note for the staff member.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Review note (visible to staff)"
            rows={3}
            data-testid="review-notes-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReviewing(null); setDecision(null); }} disabled={submitting}>Cancel</Button>
            <Button onClick={submitReview} disabled={submitting} data-testid="review-confirm-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm {decision === 'APPROVED' ? 'approval' : 'rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileBottomNav />
    </div>
  );
}
